import * as React from "react";
import {Row, Grid, Button, Form, FormGroup, Col} from "react-bootstrap";
import {WithApi, WithApiProperties} from "../common/util/WithApi";
import {defineMessages, FormattedMessage, InjectedIntlProps, injectIntl} from "react-intl";
import {getLanguageAndLocale} from "../common/intl/intl";
import Select from "../../node_modules/react-select/dist/react-select.cjs"
import {Spinner} from "../common/ui/spinner/Spinner";
import {AnalyticsResponse, Intervals, RecordsDataTypes} from "./model";
import {
    ResponsiveContainer,
    LineChart,
    Line,
    CartesianGrid,
    XAxis,
    YAxis,
    Tooltip,
    Legend
} from 'recharts';

import ReactDatePicker from "react-datepicker";
import JsPDF from 'jspdf';
import * as htmlToImage from 'html-to-image';
import {ApolloApi} from "../api/ApolloApi";
import {DateUtil} from "../common/util/DateUtil";
import {PropsValue} from "react-select";
import {AxiosError} from "axios";
import {Redirect} from "react-router-dom";
import {ErrorDisplay} from "../common/ui/errordisplay/ErrorDisplay";

const LABEL_COL = 1;
const CONTROL_COL = 2;

const MESSAGES = defineMessages({
    TODAY: {
        id: "studio.analytics.date-picker-today-label",
        defaultMessage: "Today",
    },
    ALL_USERS: {
        id: "studio.analytics.all-users-label",
        defaultMessage: "All Users",
    },
    DOWNLOADS: {
        id: "studio.analytics.downloads-label",
        defaultMessage: "Downloads",
    },
    UPLOADS: {
        id: "studio.analytics.uploads-label",
        defaultMessage: "Uploads",
    }
});


interface AnalyticsPageState {
    loading: boolean;
    error: AxiosError;
    interval: string;
    recordsDataType: string;
    download: AnalyticsResponse[];
    upload: AnalyticsResponse[];
    startDate: Date;
    endDate: Date;
    locale: any;
    analyticsEnabled: boolean;
    user: PropsValue<any>;
    users: PropsValue<any>[];
}

export class AnalyticsPageComponent extends React.Component<WithApiProperties & InjectedIntlProps, AnalyticsPageState> {
    constructor(props) {
        super(props);
        const dateToday = new Date();
        const defaultRange = 7;  //Default setting, get last 7 days

        this.state = {
            loading: true,
            error: null,
            interval: Intervals.DAYS,
            recordsDataType: RecordsDataTypes.ALL,
            download: [],
            upload: [],
            startDate: new Date(new Date().setDate(dateToday.getDate() - defaultRange)),
            endDate: dateToday,
            locale: getLanguageAndLocale(),
            analyticsEnabled: false,
            user: {value: null, label: this.props.intl.formatMessage(MESSAGES.ALL_USERS)} as PropsValue<any>,
            users: []
        };
    }

    componentDidMount() {
        Promise.allSettled([
            ApolloApi.getAllUsers(),
            ApolloApi.getStudioSettings()
        ]).then((response) => {
            let analyticsEnabled = false;
            const users = [{value: null, label: this.props.intl.formatMessage(MESSAGES.ALL_USERS)} as PropsValue<any>];
            if (response[0].status === 'fulfilled') {
                const allUsersResponse = response[0] as PromiseFulfilledResult<any>;
                allUsersResponse.value.forEach((usr) => {
                    users.push({value: usr.username, label: usr.username} as PropsValue<any>);
                });
            }
            if (response[1].status === 'fulfilled') {
                const analyticsEnabledResponse = response[1] as PromiseFulfilledResult<any>;
                analyticsEnabled = analyticsEnabledResponse.value.data.values.auditLoggingFlag === "true"
            }
            this.setState({analyticsEnabled, users});
        });
    }

    updateGraphs() {
        const {startDate, endDate, interval, recordsDataType, user} = this.state;
        let downloadResponse = [];
        let uploadResponse = [];
        const includeEndDate = new Date(new Date().setDate(endDate.getDate() + 1));
        const startYear = new Intl.DateTimeFormat('en', {year: 'numeric'}).format(startDate);
        const startMonth = new Intl.DateTimeFormat('en', {month: '2-digit'}).format(startDate);
        const startDay = new Intl.DateTimeFormat('en', {day: '2-digit'}).format(startDate);
        const endYear = new Intl.DateTimeFormat('en', {year: 'numeric'}).format(includeEndDate);
        const endMonth = new Intl.DateTimeFormat('en', {month: '2-digit'}).format(includeEndDate);
        const endDay = new Intl.DateTimeFormat('en', {day: '2-digit'}).format(includeEndDate);

        Promise.allSettled([
            this.props.api.getAnalytics({
                "logEntryType": "DATASET_DOWNLOAD",
                "interval": interval,
                "beforeTimestamp": `${endYear}-${endMonth}-${endDay}T00:00:00`,
                "onOrAfterTimestamp": `${startYear}-${startMonth}-${startDay}T00:00:00`,
                "user": user.value
            }),
            this.props.api.getAnalytics({
                "logEntryType": "DATASET_UPLOAD",
                "interval": interval,
                "beforeTimestamp": `${endYear}-${endMonth}-${endDay}T00:00:00`,
                "onOrAfterTimestamp": `${startYear}-${startMonth}-${startDay}T00:00:00`,
                "recordsDataType": recordsDataType === RecordsDataTypes.ALL ? null : recordsDataType,
                "user": user.value
            })
        ])
            .then((response) => {
                if (response[0].status === 'fulfilled') {
                    downloadResponse = response[0].value;
                }
                if (response[1].status === 'fulfilled') {
                    uploadResponse = response[1].value;
                }
                this.setState({
                    download: downloadResponse,
                    upload: uploadResponse,
                    loading: false,
                });
            })
            .catch((error) => {
                this.setState({error})
            });
    }

    private onStartDateChange = (date) => {
        //can't be after end date
        this.setState({
            startDate: date,
            loading: true,
        });

    }
    private onEndDateChange = (date) => {
        //can't be after today
        this.setState({
            endDate: date,
            loading: true,
        });
    }

    private onChangeTimeBin = (newTimeBin) => {
        this.setState({
            interval: newTimeBin.value,
            loading: true,
        });
    }

    private onChangeRecordsDataType = (newRecordDataType) => {
        this.setState({
            recordsDataType: newRecordDataType.value,
            loading: true,
        });
    }

    private onChangeUser = (newUser) => {
        this.setState({
            user: newUser,
            loading: true,
        });
    }

    render() {
        return (this.renderContent());
    }

    private renderContent() {
        const {
            startDate,
            endDate,
            loading,
            interval,
            recordsDataType,
            error,
            analyticsEnabled,
            user,
            users
        } = this.state;
        const {intl} = this.props;

        if (error) {
            if (error?.response.status === 401) {
                return <Redirect to="/login"/>;
            }
            return <ErrorDisplay error={error}/>;
        }

        if (!analyticsEnabled) {
            return AnalyticsPageComponent.renderAnalyticsError();
        }

        if (loading) {
            this.updateGraphs();
            return AnalyticsPageComponent.renderLoading();
        }

        const today = this.props.intl.formatMessage(MESSAGES.TODAY);
        const {locale} = getLanguageAndLocale();

        const intervals = [];
        for (const key in Intervals) {
            if (Object.prototype.hasOwnProperty.call(Intervals, key)) {
                intervals.push({value: Intervals[key], label: Intervals[key]});
            }
        }

        const recordsDataTypes = [];
        for (const key in RecordsDataTypes) {
            if (Object.prototype.hasOwnProperty.call(RecordsDataTypes, key)) {
                recordsDataTypes.push({value: RecordsDataTypes[key], label: RecordsDataTypes[key]});
            }
        }

        return (
            <Grid fluid={true}>
                <div>
                    <h2><FormattedMessage id="studio.analytics.title" defaultMessage="Analytics"/></h2>
                    <hr/>
                    <Form horizontal className="settings-form">
                        <FormGroup>
                            <Col sm={LABEL_COL}>
                                <FormattedMessage id="studio.analytics.start" defaultMessage="Start Date:"/>
                            </Col>
                            <Col sm={CONTROL_COL}>
                                <ReactDatePicker
                                    onChange={this.onStartDateChange}
                                    selected={startDate}
                                    todayButton={today}
                                    locale={locale}
                                    dateFormat={`${DateUtil.dateFormat()}`}
                                />
                            </Col>
                            <Col sm={LABEL_COL}>
                                <FormattedMessage id="studio.analytics.end" defaultMessage="End Date:"/>
                            </Col>
                            <Col sm={CONTROL_COL}>
                                <ReactDatePicker
                                    onChange={this.onEndDateChange}
                                    selected={endDate}
                                    todayButton={today}
                                    locale={locale}
                                    dateFormat={`${DateUtil.dateFormat()}`}
                                />
                            </Col>
                        </FormGroup>
                        <FormGroup>
                            <Col sm={LABEL_COL}>
                                <FormattedMessage id="studio.analytics.interval" defaultMessage="Interval:"/>
                            </Col>
                            <Col sm={CONTROL_COL}>
                                <Select defaultValue={{value: interval, label: interval} as PropsValue<any>}
                                        options={intervals}
                                        onChange={this.onChangeTimeBin}
                                />
                            </Col>
                            <Col sm={LABEL_COL}>
                                <FormattedMessage id="studio.analytics.uploads_data_Type"
                                                  defaultMessage="Uploads Data Type:"/>
                            </Col>
                            <Col sm={CONTROL_COL}>
                                <Select
                                    defaultValue={{value: recordsDataType, label: recordsDataType} as PropsValue<any>}
                                    options={recordsDataTypes}
                                    onChange={this.onChangeRecordsDataType}
                                />
                            </Col>
                            <Col sm={LABEL_COL}>
                                <FormattedMessage id="studio.analytics.user" defaultMessage="User:"/>
                            </Col>
                            <Col sm={CONTROL_COL}>
                                <Select
                                    defaultValue={user}
                                    options={users}
                                    onChange={this.onChangeUser}
                                />
                            </Col>
                        </FormGroup>
                    </Form>
                    <hr/>
                </div>
                <div>
                    <Button onClick={() => this.createPDF(intl.formatMessage(MESSAGES.DOWNLOADS))}>
                        <FormattedMessage id="studio.analytics.PDF" defaultMessage="PDF"/>
                    </Button>
                    {AnalyticsPageComponent.renderGraph(intl.formatMessage(MESSAGES.DOWNLOADS), this.state.download)}
                </div>
                <div>
                    <Button onClick={() => this.createPDF(intl.formatMessage(MESSAGES.UPLOADS))}>
                        <FormattedMessage id="studio.analytics.PDF" defaultMessage="PDF"/>
                    </Button>
                    {AnalyticsPageComponent.renderGraph(intl.formatMessage(MESSAGES.UPLOADS), this.state.upload)}
                </div>
            </Grid>
        );
    }

    private createPDF = (title: string) => {
        htmlToImage.toPng(document.getElementById(title + "_PDF")).then((image) => {
            const doc = new JsPDF();

            //header
            doc.setDrawColor(255, 255, 255);
            doc.setFillColor(225, 225, 225);
            doc.roundedRect(20, 20, 175, 12, 3, 3, 'FD');
            doc.setFontSize(22);
            doc.text(title, 30, 28);

            //graph
            doc.addImage(image, 'png', 20, 40, 175, 75);

            //footer
            doc.setFontSize(11);
            doc.text(DateUtil.formatDateTime(new Date()), 20, 120);
            doc.save(title);
        });
    }

    private static renderGraph(title: string, data: AnalyticsResponse[]) {
        const displayData = [];

        data.forEach(value => {
            displayData.push({
                label: DateUtil.formatDate(value.date),
                [title.toString()]: value.count
            })
        });

        return (
            <div className="section col-md-6" style={{width: "100%", height: "100%"}}>
                <h3 className="section-title">{title}</h3>
                <div id={`${title}_PDF`} className="section-content">
                    <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={displayData} margin={{top: 15, right: 0, bottom: 15, left: 0}}>
                            <Tooltip/>
                            <XAxis dataKey="label"/>
                            <YAxis/>
                            <CartesianGrid stroke="#ccc" strokeDasharray="5 5"/>
                            <Legend/>
                            <Line type="monotone" dataKey={title.toString()} stroke="#FB8833"/>
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            </div>
        );
    }


    private static renderLoading() {
        return (
            <Row style={{textAlign: "center"}}>
                <h2>
                    <FormattedMessage id="studio.settings.analytics-service.loading"
                                      defaultMessage="Loading..."/>
                </h2>
                <Spinner bigStyle/>
            </Row>
        );
    }

    private static renderAnalyticsError() {
        return (
            <div>
                <div>
                    <strong>
                        <FormattedMessage id="studio.settings.analytics-service.EnableAuditLogging"
                                          defaultMessage="Enable audit logging"/>
                    </strong>
                    <FormattedMessage id="studio.settings.analytics-service.not-enabled"
                                      defaultMessage=" must be enabled to view Analytics."/>
                    <br/>
                    <a href={"#/settings/config?page=General"}><FormattedMessage
                        id="studio.settings.analytics-service.settings"
                        defaultMessage="Open Settings page"/></a>
                </div>
            </div>
        );
    }
}

export const AnalyticsPage = injectIntl(WithApi(AnalyticsPageComponent));